import { useState, useRef } from 'react'
import { Camera, Upload, X, Loader, FileText, Check, AlertCircle } from 'lucide-react'

export default function InvoiceScanner({ vehicles = [], onExtracted, onClose }) {
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState(null)
  const [extracting, setExtracting] = useState(false)
  const [error, setError] = useState('')
  const [extracted, setExtracted] = useState(null)
  const fileInputRef = useRef(null)
  const cameraInputRef = useRef(null)

  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    ('ontouchstart' in window && window.innerWidth < 1024)

  const handleFile = (selectedFile) => {
    if (!selectedFile) return
    setFile(selectedFile)
    setError('')
    setExtracted(null)

    // Create preview
    const reader = new FileReader()
    reader.onloadend = () => setPreview(reader.result)
    reader.readAsDataURL(selectedFile)
  }

  const handleExtract = async () => {
    if (!file) return
    setExtracting(true)
    setError('')

    try {
      // Convert file to base64
      const base64 = await new Promise((resolve) => {
        const reader = new FileReader()
        reader.onloadend = () => {
          const base64String = reader.result.split(',')[1]
          resolve(base64String)
        }
        reader.readAsDataURL(file)
      })

      // Send to API for extraction
      const response = await fetch('/api/concierge/extract-invoice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image: base64,
          mimeType: file.type || 'image/jpeg',
          vehicles: vehicles.map(v => ({
            id: v.id,
            label: `${v.year} ${v.make} ${v.model}`
          }))
        })
      })

      const data = await response.json()
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to extract invoice data')
      }

      setExtracted(data.extracted)
    } catch (err) {
      console.error('Invoice extraction error:', err)
      setError(err.message || 'Failed to read invoice. Try a clearer photo.')
    } finally {
      setExtracting(false)
    }
  }

  const handleUseExtracted = () => {
    if (extracted && onExtracted) {
      onExtracted(extracted)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <div>
            <h3 className="text-lg font-display font-semibold text-black">Scan Service Invoice</h3>
            <p className="text-xs text-rdc-taupe mt-0.5">Take a photo or upload an invoice to auto-fill service details</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-full">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* File Input Area */}
          {!preview ? (
            <div className="space-y-3">
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center cursor-pointer hover:border-rdc-primary/40 hover:bg-rdc-primary/5 transition-colors"
              >
                <Upload size={32} className="text-rdc-taupe mx-auto mb-3" />
                <p className="text-sm font-medium text-black mb-1">
                  {isMobile ? 'Choose from library' : 'Upload invoice photo'}
                </p>
                <p className="text-xs text-rdc-taupe">JPG, PNG, or PDF</p>
              </div>

              {isMobile && (
                <button
                  onClick={() => cameraInputRef.current?.click()}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-rdc-primary text-white rounded-xl font-medium text-sm hover:bg-rdc-primary/90 transition-colors"
                >
                  <Camera size={18} />
                  Take a Photo
                </button>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="hidden"
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="hidden"
              />
            </div>
          ) : (
            <>
              {/* Preview */}
              <div className="relative">
                <img
                  src={preview}
                  alt="Invoice preview"
                  className="w-full rounded-lg border border-gray-200 max-h-64 object-contain bg-gray-50"
                />
                <button
                  onClick={() => {
                    setFile(null)
                    setPreview(null)
                    setExtracted(null)
                    setError('')
                  }}
                  className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow-md hover:bg-gray-100"
                >
                  <X size={14} />
                </button>
              </div>

              {/* Extract button */}
              {!extracted && !extracting && (
                <button
                  onClick={handleExtract}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-rdc-primary text-white rounded-xl font-medium text-sm hover:bg-rdc-primary/90 transition-colors"
                >
                  <FileText size={18} />
                  Extract Invoice Details
                </button>
              )}

              {/* Extracting state */}
              {extracting && (
                <div className="flex items-center justify-center gap-3 py-4">
                  <Loader size={18} className="animate-spin text-rdc-primary" />
                  <span className="text-sm text-rdc-taupe">Reading invoice with AI...</span>
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-lg">
                  <AlertCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Extracted data preview */}
              {extracted && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-green-700">
                    <Check size={18} />
                    <span className="text-sm font-medium">Invoice details extracted</span>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 space-y-2.5">
                    {extracted.vendor_name && (
                      <div className="flex justify-between text-sm">
                        <span className="text-rdc-taupe">Shop / Vendor</span>
                        <span className="font-medium text-black">{extracted.vendor_name}</span>
                      </div>
                    )}
                    {extracted.service_date && (
                      <div className="flex justify-between text-sm">
                        <span className="text-rdc-taupe">Date</span>
                        <span className="font-medium text-black">{extracted.service_date}</span>
                      </div>
                    )}
                    {extracted.service_type && (
                      <div className="flex justify-between text-sm">
                        <span className="text-rdc-taupe">Service Type</span>
                        <span className="font-medium text-black">{extracted.service_type}</span>
                      </div>
                    )}
                    {extracted.description && (
                      <div className="text-sm">
                        <span className="text-rdc-taupe block mb-1">Description</span>
                        <span className="text-black">{extracted.description}</span>
                      </div>
                    )}
                    {extracted.total_cost && (
                      <div className="flex justify-between text-sm">
                        <span className="text-rdc-taupe">Total Cost</span>
                        <span className="font-semibold text-black">${Number(extracted.total_cost).toLocaleString()}</span>
                      </div>
                    )}
                    {extracted.mileage && (
                      <div className="flex justify-between text-sm">
                        <span className="text-rdc-taupe">Mileage at Service</span>
                        <span className="font-medium text-black">{Number(extracted.mileage).toLocaleString()}</span>
                      </div>
                    )}
                    {extracted.matched_vehicle && (
                      <div className="flex justify-between text-sm">
                        <span className="text-rdc-taupe">Vehicle</span>
                        <span className="font-medium text-black">{extracted.matched_vehicle}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setFile(null)
                        setPreview(null)
                        setExtracted(null)
                      }}
                      className="btn-secondary flex-1 text-sm"
                    >
                      Try Again
                    </button>
                    <button
                      onClick={handleUseExtracted}
                      className="btn-primary flex-1 text-sm"
                    >
                      Use These Details
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
